import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { toast } from "sonner";
import { Wallet, Zap, QrCode, History, Globe, ArrowRight, Phone, ArrowLeft, Loader2, Mail, ShieldCheck } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { t, lang, toggleLang } = useApp();
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState("main"); // main | phone
  const [step, setStep] = useState(1); // 1 phone, 2 code
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [googleClientId, setGoogleClientId] = useState(null);
  const [emailMode, setEmailMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [nationalId, setNationalId] = useState("");
  const [regMobile, setRegMobile] = useState("");
  const [accountType, setAccountType] = useState("individual");
  const [crNumber, setCrNumber] = useState("");

  useEffect(() => {
    api.get("/auth/config").then((r) => {
      setOtpEnabled(!!r.data.otp_enabled);
      setGoogleClientId(r.data.google_enabled ? r.data.google_client_id : null);
    }).catch(() => {});
  }, []);

  const submitEmail = async () => {
    if (!email.trim() || !password) return;
    setLoading(true);
    try {
      const endpoint = emailMode === "register" ? "/auth/register" : "/auth/login";
      const payload = emailMode === "register"
        ? { name, email: email.trim(), password, mobile: regMobile, account_type: accountType,
            national_id: accountType === "individual" ? nationalId : "",
            cr_number: accountType === "business" ? crNumber : "" }
        : { email: email.trim(), password };
      const res = await api.post(endpoint, payload);
      setUser(res.data.user);
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  // Self-hosted Google Sign-In (Google Identity Services). Only active when the
  // backend reports a GOOGLE_CLIENT_ID; otherwise the button is hidden.
  const handleGoogle = () => {
    if (!googleClientId) return;
    const start = () => {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async ({ credential }) => {
          try {
            const res = await api.post("/auth/google", { credential });
            setUser(res.data.user);
            navigate("/", { replace: true });
          } catch (e) {
            toast.error(e.response?.data?.detail || t.errorGeneric);
          }
        },
      });
      window.google.accounts.id.prompt();
    };
    if (window.google?.accounts?.id) return start();
    const sc = document.createElement("script");
    sc.src = "https://accounts.google.com/gsi/client";
    sc.onload = start;
    document.head.appendChild(sc);
  };

  const sendCode = async () => {
    setLoading(true);
    try {
      const res = await api.post("/auth/otp/request", { phone });
      setStep(2);
      setOtp("");
      if (res.data.demo && res.data.demo_code) {
        toast.success(`${t.demoCodeHint}: ${res.data.demo_code}`, { duration: 8000 });
      } else {
        toast.success(t.sendCode);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (code) => {
    setLoading(true);
    try {
      const res = await api.post("/auth/otp/verify", { phone, code });
      setUser(res.data.user);
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
      setOtp("");
    } finally {
      setLoading(false);
    }
  };

  const bullets = [
    { icon: Zap, text: t.loginBullet1 },
    { icon: QrCode, text: t.loginBullet2 },
    { icon: History, text: t.loginBullet3 },
  ];

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white relative overflow-hidden flex flex-col">
      <div className="absolute inset-0 card-shine opacity-80 pointer-events-none" />
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-emerald-600/20 blur-3xl" />
      <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-emerald-500/10 blur-3xl" />

      <div className="relative z-10 max-w-md mx-auto w-full flex-1 flex flex-col px-6 pt-6 pb-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-700 grid place-items-center shadow-lg shadow-emerald-900/50">
              <Wallet className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-extrabold tracking-tight">
              Sb<span className="text-emerald-400">Pay</span>
            </span>
          </div>
          <button
            data-testid="language-switch-toggle"
            onClick={toggleLang}
            className="flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <Globe className="w-4 h-4" />
            {lang === "ar" ? "EN" : "ع"}
          </button>
        </div>

        <AnimatePresence mode="wait">
          {view === "main" ? (
            <motion.div
              key="main"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col"
            >
              <div className="flex-1 flex flex-col justify-center">
                <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                  <div className="mx-auto w-20 h-20 rounded-3xl bg-gradient-to-br from-emerald-400 to-emerald-700 grid place-items-center shadow-2xl shadow-emerald-900/50 mb-8">
                    <Wallet className="w-10 h-10 text-white" />
                  </div>
                  <h1 className="text-4xl font-black tracking-tight text-center leading-tight">
                    Sb<span className="text-emerald-400">Pay</span>
                  </h1>
                  <p className="text-center text-white/60 mt-3 text-base px-4">{t.tagline}</p>
                </motion.div>

                <div className="mt-10 space-y-3">
                  {bullets.map((b, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: lang === "ar" ? 20 : -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.15 + i * 0.1 }}
                      className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl p-3.5"
                    >
                      <div className="w-9 h-9 rounded-xl bg-emerald-500/15 grid place-items-center shrink-0">
                        <b.icon className="w-4 h-4 text-emerald-400" />
                      </div>
                      <span className="text-sm font-medium text-white/85">{b.text}</span>
                    </motion.div>
                  ))}
                </div>
              </div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="space-y-3">
                {googleClientId && (
                <Button
                  data-testid="google-signin-button"
                  onClick={handleGoogle}
                  className="w-full h-14 rounded-2xl bg-white text-[#0B0F17] hover:bg-white/90 text-base font-bold shadow-xl group"
                >
                  <svg className="w-5 h-5 me-1" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.76c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
                  </svg>
                  {t.signInGoogle}
                  <ArrowRight className="w-5 h-5 ms-1 rtl:rotate-180 transition-transform group-hover:translate-x-0.5" />
                </Button>
                )}

                <div className="flex items-center gap-3 py-1">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-xs text-white/40">{t.orDivider}</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>

                <Button
                  data-testid="email-signin-button"
                  onClick={() => { setView("email"); setEmailMode("login"); }}
                  variant="outline"
                  className="w-full h-14 rounded-2xl bg-white/5 border-white/15 text-white hover:bg-white/10 text-base font-bold"
                >
                  <Mail className="w-5 h-5 me-1" /> {t.continueWithEmail}
                </Button>

                {otpEnabled && (
                  <Button
                    data-testid="phone-signin-button"
                    onClick={() => { setView("phone"); setStep(1); }}
                    variant="outline"
                    className="w-full h-14 rounded-2xl bg-white/5 border-white/15 text-white hover:bg-white/10 text-base font-bold"
                  >
                    <Phone className="w-5 h-5 me-1" /> {t.phoneLogin}
                  </Button>
                )}
                <p className="text-center text-xs text-white/40 mt-2">{t.brand} · {lang === "ar" ? "وضع تجريبي" : "Demo Mode"}</p>
              </motion.div>
            </motion.div>
          ) : view === "email" ? (
            <motion.div
              key="email"
              initial={{ opacity: 0, x: lang === "ar" ? -30 : 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col justify-center"
            >
              <button
                data-testid="email-back-button"
                onClick={() => setView("main")}
                className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm font-semibold mb-8"
              >
                <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t.backToGoogle}
              </button>

              <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-500/15 grid place-items-center mb-6">
                <Mail className="w-8 h-8 text-emerald-400" />
              </div>

              <div className="grid grid-cols-2 gap-1 p-1 rounded-2xl bg-white/5 mb-6">
                {["login", "register"].map((m) => (
                  <button
                    key={m}
                    data-testid={`email-tab-${m}`}
                    onClick={() => setEmailMode(m)}
                    className={`h-10 rounded-xl text-sm font-bold transition-colors ${emailMode === m ? "bg-emerald-500 text-white" : "text-white/60 hover:text-white"}`}
                  >
                    {m === "login" ? t.loginTab : t.registerTab}
                  </button>
                ))}
              </div>

              <form
                onSubmit={(e) => { e.preventDefault(); submitEmail(); }}
                className="space-y-3"
              >
                {emailMode === "register" && (
                  <div>
                    <label className="text-xs font-semibold text-white/60">{t.fullName}</label>
                    <Input
                      data-testid="email-name-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t.namePlaceholder}
                      className="h-13 h-[52px] rounded-2xl bg-white/5 border-white/15 text-white mt-1.5 placeholder:text-white/30"
                    />
                  </div>
                )}
                {emailMode === "register" && (
                  <div>
                    <label className="text-xs font-semibold text-white/60">{t.accountType}</label>
                    <div className="grid grid-cols-2 gap-1 p-1 rounded-2xl bg-white/5 mt-1.5">
                      {["individual", "business"].map((at) => (
                        <button
                          key={at}
                          type="button"
                          data-testid={`account-type-${at}`}
                          onClick={() => setAccountType(at)}
                          className={`h-10 rounded-xl text-sm font-bold transition-colors ${accountType === at ? "bg-emerald-500 text-white" : "text-white/60 hover:text-white"}`}
                        >
                          {at === "individual" ? t.individual : t.business}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {emailMode === "register" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs font-semibold text-white/60">
                        {accountType === "business" ? t.crNumber : t.nationalId}
                      </label>
                      {accountType === "business" ? (
                        <Input
                          data-testid="email-cr-input"
                          dir="ltr"
                          inputMode="numeric"
                          value={crNumber}
                          onChange={(e) => setCrNumber(e.target.value)}
                          placeholder={t.crPlaceholder}
                          className="h-[52px] rounded-2xl bg-white/5 border-white/15 text-white mt-1.5 placeholder:text-white/30 tabular-nums"
                        />
                      ) : (
                        <Input
                          data-testid="email-nid-input"
                          dir="ltr"
                          inputMode="numeric"
                          value={nationalId}
                          onChange={(e) => setNationalId(e.target.value)}
                          placeholder={t.nationalIdPlaceholder}
                          className="h-[52px] rounded-2xl bg-white/5 border-white/15 text-white mt-1.5 placeholder:text-white/30 tabular-nums"
                        />
                      )}
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-white/60">{t.mobileOptional}</label>
                      <Input
                        data-testid="email-mobile-input"
                        dir="ltr"
                        inputMode="numeric"
                        value={regMobile}
                        onChange={(e) => setRegMobile(e.target.value)}
                        placeholder="05XXXXXXXX"
                        className="h-[52px] rounded-2xl bg-white/5 border-white/15 text-white mt-1.5 placeholder:text-white/30 tabular-nums"
                      />
                    </div>
                  </div>
                )}
                {emailMode === "register" && (
                  <p className="flex items-center gap-1.5 text-[11px] text-emerald-400/80">
                    <ShieldCheck className="w-3.5 h-3.5 shrink-0" /> {t.nafathVerifyNote}
                  </p>
                )}
                <div>
                  <label className="text-xs font-semibold text-white/60">{t.emailLabel}</label>
                  <Input
                    data-testid="email-input"
                    type="email"
                    dir="ltr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t.emailPlaceholder}
                    className="h-[52px] rounded-2xl bg-white/5 border-white/15 text-white mt-1.5 placeholder:text-white/30"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-white/60">{t.passwordLabel}</label>
                  <Input
                    data-testid="password-input"
                    type="password"
                    dir="ltr"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t.passwordPlaceholder}
                    className="h-[52px] rounded-2xl bg-white/5 border-white/15 text-white mt-1.5 placeholder:text-white/30"
                  />
                </div>
                <Button
                  type="submit"
                  data-testid="email-submit-button"
                  disabled={loading || !email || !password || (emailMode === "register" && (!name || (accountType === "individual" ? nationalId.replace(/\D/g, "").length !== 10 : crNumber.replace(/\D/g, "").length !== 10)))}
                  className="w-full h-14 rounded-2xl text-base font-bold mt-2 bg-emerald-500 hover:bg-emerald-600 text-white"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (emailMode === "register" ? t.registerBtn : t.signInBtn)}
                </Button>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="phone"
              initial={{ opacity: 0, x: lang === "ar" ? -30 : 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              className="flex-1 flex flex-col justify-center"
            >
              <button
                data-testid="phone-back-button"
                onClick={() => (step === 2 ? setStep(1) : setView("main"))}
                className="flex items-center gap-1.5 text-white/60 hover:text-white text-sm font-semibold mb-8"
              >
                <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {step === 2 ? t.changeNumber : t.backToGoogle}
              </button>

              <div className="mx-auto w-16 h-16 rounded-2xl bg-emerald-500/15 grid place-items-center mb-6">
                <Phone className="w-8 h-8 text-emerald-400" />
              </div>

              {step === 1 ? (
                <>
                  <h2 className="text-2xl font-black text-center">{t.enterPhoneTitle}</h2>
                  <p className="text-center text-white/60 mt-2 text-sm">{t.enterPhoneDesc}</p>
                  <div className="mt-8">
                    <Input
                      data-testid="phone-input"
                      type="tel"
                      inputMode="numeric"
                      dir="ltr"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder={t.phonePlaceholder}
                      className="h-14 rounded-2xl bg-white/5 border-white/15 text-white text-center text-lg tracking-widest tabular-nums placeholder:text-white/30"
                    />
                    <Button
                      data-testid="send-otp-button"
                      onClick={sendCode}
                      disabled={loading || phone.replace(/\D/g, "").length < 9}
                      className="w-full h-14 rounded-2xl text-base font-bold mt-4 bg-emerald-500 hover:bg-emerald-600 text-white"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t.sendCode}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-2xl font-black text-center">{t.enterCodeTitle}</h2>
                  <p className="text-center text-white/60 mt-2 text-sm">{t.enterCodeDesc}</p>
                  <div className="mt-8 flex flex-col items-center" dir="ltr">
                    <InputOTP
                      maxLength={6}
                      value={otp}
                      onChange={(v) => { setOtp(v); if (v.length === 6) verify(v); }}
                      data-testid="otp-input"
                    >
                      <InputOTPGroup>
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                          <InputOTPSlot key={i} index={i} className="w-11 h-14 text-xl bg-white/5 border-white/20 text-white" />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                    <Button
                      data-testid="verify-otp-button"
                      onClick={() => verify(otp)}
                      disabled={loading || otp.length < 6}
                      className="w-full h-14 rounded-2xl text-base font-bold mt-6 bg-emerald-500 hover:bg-emerald-600 text-white"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t.verifyCode}
                    </Button>
                    <button
                      data-testid="resend-otp-button"
                      onClick={sendCode}
                      disabled={loading}
                      className="text-sm text-emerald-400 hover:text-emerald-300 font-semibold mt-4"
                    >
                      {t.resendCode}
                    </button>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
